import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut, 
  ChevronDown,
  Building2,
  Plus
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, empresaActual, logout, cambiarEmpresa, hasPermission } = useAuth();

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/presupuestos', label: 'Presupuestos', icon: FileText },
    { path: '/clientes', label: 'Clientes', icon: Users },
    { path: '/configuracion', label: 'Configuración', icon: Settings, roles: ['OWNER'] },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="w-64 bg-white border-r border-slate-200 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Presupuesto Pro
          </span>
        </Link>
      </div>

      {/* Empresa selector */}
      {user?.empresas && user.empresas.length > 0 && (
        <div className="p-4 border-b border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                <div className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 shrink-0" />
                  <span className="truncate">{empresaActual?.nombre_comercial || 'Seleccionar'}</span>
                </div>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              {user.empresas.map((empresa) => (
                <DropdownMenuItem 
                  key={empresa.id} 
                  onClick={() => cambiarEmpresa(empresa)}
                  className={empresa.id === empresaActual?.id ? 'bg-slate-100' : ''}
                >
                  <div className="flex flex-col">
                    <span>{empresa.nombre_comercial}</span>
                    <span className="text-xs text-slate-500">{empresa.rol}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* CTA Button */}
      <div className="p-4">
        <Button 
          className="w-full bg-blue-600 hover:bg-blue-700"
          onClick={() => navigate('/presupuestos/nuevo')}
          data-testid="sidebar-new-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Presupuesto
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {navItems.map((item) => {
            if (item.roles && !hasPermission(item.roles)) return null;
            
            const isActive = location.pathname === item.path || 
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User menu */}
      <div className="p-4 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center mr-2">
                <span className="text-sm font-medium text-slate-600">
                  {user?.nombre?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium">{user?.nombre}</span>
                <span className="text-xs text-slate-500">{user?.email}</span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/configuracion')}>
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};

export const DashboardLayout = ({ children }) => {
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 p-8 overflow-auto">
        {children}
      </main>
    </div>
  );
};
